// Live's advanced settings for a video (#231, `advanced-form-content`, `submit-utils.ts`): the
// seed, the frame rate, the guidance scale, the negative prompt and the prompt enhancer, each
// shown and sent only where the type's block takes it (G6 `advanced`).
import type {
  AdvancedCaps,
  AdvancedSettings,
  CatalogImageModel,
  CatalogVideoModel,
  GenerationType,
} from "../../../api/generated/model";

/** The settings as the composer holds them; an empty one is not sent. */
export interface AdvancedDraft {
  seed: number | null;
  fps: number | null;
  cfgScale: number | null;
  /** Live's "Negative prompt" switch, which shows the text under the prompt. */
  negative: boolean;
  negativeText: string;
  /** Live's "Enhance prompt", off by default. */
  enhance: boolean;
}

export const NO_ADVANCED: AdvancedDraft = {
  seed: null,
  fps: null,
  cfgScale: null,
  negative: false,
  negativeText: "",
  enhance: false,
};

/** The type's advanced caps, or null where it takes none. */
export function advancedCapsOf(
  model: CatalogImageModel | CatalogVideoModel,
  type: GenerationType,
): AdvancedCaps | null {
  const caps = model.medium === "video" ? model.modes[type]?.advanced : undefined;
  return caps === undefined || !takesAny(caps) ? null : caps;
}

/** Whether the caps have a setting live's panel shows. */
function takesAny(caps: AdvancedCaps): boolean {
  return (
    caps.seed === true ||
    (caps.fps?.length ?? 0) > 0 ||
    (caps.cfg_scale !== null && caps.cfg_scale !== undefined) ||
    (caps.negative_prompt !== null && caps.negative_prompt !== undefined) ||
    caps.prompt_enhancer === true
  );
}

/**
 * The settings a start sends, as live's `buildAdvancedPayload`: a set seed, frame rate and
 * guidance scale; the enhancer's on or off; the negative prompt when its switch is on and it has
 * text. Null when none is.
 */
export function advancedOf(draft: AdvancedDraft, caps: AdvancedCaps | null): AdvancedSettings | null {
  if (caps === null) return null;
  const sent: AdvancedSettings = {};
  if (caps.seed === true && draft.seed !== null) sent.seed = draft.seed;
  if ((caps.fps?.length ?? 0) > 0 && draft.fps !== null) sent.fps = draft.fps;
  if (caps.cfg_scale !== null && caps.cfg_scale !== undefined && draft.cfgScale !== null) {
    sent.cfg_scale = draft.cfgScale;
  }
  if (caps.prompt_enhancer === true) sent.prompt_enhancer = draft.enhance;
  const text = draft.negativeText.trim();
  if (caps.negative_prompt !== null && caps.negative_prompt !== undefined && draft.negative) {
    if (text.length > 0) sent.negative_prompt = text;
  }
  return Object.keys(sent).length > 0 ? sent : null;
}
